// UserMenu — right-aligned cluster in the app header.
//
// Two states:
//   1. Signed in (user has a non-null email):
//        button-styled <details>/<summary> with email handle, opens to a
//        small list with "Profile" link + "Sign out" action.
//   2. Anonymous (no user OR user without email):
//        small inline form to attach an email to the anonymous cookie.
//
// Slice 1 intentionally avoids radix — we'll add @radix-ui/react-popover
// in a later slice when we need real Dialog/Popover primitives.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { meQuery } from "@/lib/queries";
import { useAttachEmail, useLogout } from "@/lib/mutations";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";

function emailHandle(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

export function UserMenu() {
  const { data, isPending } = useQuery(meQuery);

  if (isPending || !data) {
    return <div className="h-9 w-24 animate-pulse rounded-md bg-sand-100/60" />;
  }

  const handle = emailHandle(data.user?.email);

  if (handle) {
    return <SignedInMenu handle={handle} />;
  }
  return <AttachEmailForm />;
}

function SignedInMenu({ handle }: { handle: string }) {
  const router = useRouter();
  const logout = useLogout();

  async function onLogout() {
    if (logout.isPending) return;
    await logout.mutateAsync();
    await router.navigate({ to: "/login" });
  }

  return (
    <details className="group relative">
      <summary
        className={cn(
          "list-none cursor-pointer select-none",
          "inline-flex items-center gap-2 rounded-md border border-input bg-background/70",
          "px-3 h-9 font-mono text-sm text-foreground",
          "hover:bg-sand-100",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "[&::-webkit-details-marker]:hidden",
        )}
      >
        <span aria-hidden="true">👤</span>
        <span>{handle}</span>
        <span aria-hidden="true" className="text-xs">▾</span>
      </summary>
      <div
        className={cn(
          "absolute right-0 z-20 mt-2 min-w-[12rem] rounded-md border border-border",
          "bg-card p-1 shadow-lg",
        )}
        role="menu"
      >
        <Link
          to="/help/rules"
          role="menuitem"
          className="block rounded-sm px-3 py-2 font-mono text-sm text-foreground hover:bg-sand-100"
          onClick={(e) => {
            const root = (e.currentTarget.closest("details") as HTMLDetailsElement | null);
            if (root) root.open = false;
          }}
        >
          How to play
        </Link>
        <Link
          to="/profile"
          role="menuitem"
          className="block rounded-sm px-3 py-2 font-mono text-sm text-foreground hover:bg-sand-100"
          onClick={(e) => {
            // Close the dropdown when navigating.
            const root = (e.currentTarget.closest("details") as HTMLDetailsElement | null);
            if (root) root.open = false;
          }}
        >
          Profile
        </Link>
        <button
          type="button"
          role="menuitem"
          onClick={onLogout}
          disabled={logout.isPending}
          className="block w-full text-left rounded-sm px-3 py-2 font-mono text-sm text-destructive hover:bg-destructive/10 disabled:opacity-60"
        >
          {logout.isPending ? "Signing out…" : "Sign out"}
        </button>
      </div>
    </details>
  );
}

function AttachEmailForm() {
  const attach = useAttachEmail();
  const [email, setEmail] = useState("");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || attach.isPending) return;
    try {
      await attach.mutateAsync(trimmed);
      setEmail("");
    } catch {
      // shown via attach.error below
    }
  }

  const errorMessage =
    attach.error instanceof ApiError ? attach.error.message : null;

  return (
    <div className="flex flex-col items-end gap-1">
      <form onSubmit={onSubmit} className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          <Input
            aria-label="Tag your hat with an email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="tag your hat — drop your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-9 w-56 text-sm"
          />
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            disabled={attach.isPending || email.trim().length === 0}
          >
            {attach.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
        {errorMessage && (
          <span role="alert" className="font-mono text-xs text-destructive">
            {errorMessage}
          </span>
        )}
      </form>
      <Link
        to="/help/rules"
        className="font-mono text-[11px] text-muted-foreground hover:text-foreground hover:underline"
      >
        How to play
      </Link>
    </div>
  );
}
